// src/components/Login.js

import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faUser, faLock, faKey, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../SocketContext';
import './AuthPage.css';

const Login = () => {
    const { t } = useTranslation(['auth', 'common']);
    const [formData, setFormData] = useState({
        identifier: '',
        password: '',
        otpToken: '',
    });
    const [message, setMessage] = useState('');
    const [show2faForm, setShow2faForm] = useState(false);

    const { connectSocket = () => {}, disconnectSocket = () => {}, fetchNotifications = () => {} } = useSocket() || {};

    const navigate = useNavigate();
    const location = useLocation();

    const { identifier, password, otpToken } = formData;

    const onChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const completeLogin = (token, user) => {
        disconnectSocket();

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));

        // Notifier le ThemeContext du changement d'utilisateur
        window.dispatchEvent(new Event('user:changed'));

        // 🆕 Notifier le Navbar que l'état d'authentification a changé
        window.dispatchEvent(new Event('authChanged'));

        setTimeout(() => {
            connectSocket();
            fetchNotifications();
        }, 100);

        const redirectTo = location.state?.from || '/dashboard';
        navigate(redirectTo, { replace: true });
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setMessage('');

        const cleanedIdentifier = identifier.trim();
        const cleanedPassword = password.trim();

        try {
            const config = {
                headers: { 'Content-Type': 'application/json' }
            };

            const body = JSON.stringify({
                identifier: cleanedIdentifier,
                password: cleanedPassword
            });

            const res = await axios.post('https://api.insulated.pro/api/auth/login', body, config);

            if (res.data.requires2fa) {
                setMessage(res.data.message);
                setShow2faForm(true);
                return;
            }

            completeLogin(res.data.token, res.data.user);

        } catch (err) {
            console.error('Erreur de la requête:', err);

            if (err.response) {
                setMessage(err.response.data.message || err.response.data.msg || t('auth:login.error_invalid_credentials'));
            } else {
                setMessage(t('common:errors.network'));
            }
        }
    };

    const onSubmit2fa = async (e) => {
        e.preventDefault();
        setMessage('');

        try {
            const config = {
                headers: { 'Content-Type': 'application/json' }
            };

            const body = JSON.stringify({
                identifier: identifier.trim(),
                token: otpToken.trim()
            });

            const res = await axios.post('https://api.insulated.pro/api/auth/2fa/login-verify', body, config);

            completeLogin(res.data.token, res.data.user);

        } catch (err) {
            console.error('Erreur de vérification 2FA:', err);
            if (err.response) {
                setMessage(err.response.data.msg || t('auth:two_factor.error_invalid_code'));
            } else {
                setMessage(t('common:errors.server'));
            }
        }
    };

    const handleGoogleLogin = () => {
        window.location.href = 'https://api.insulated.pro/api/auth/google';
    };

    return (
        <div className="auth-page">
            <div className="auth-orb auth-orb-1"></div>
            <div className="auth-orb auth-orb-2"></div>
            <div className="auth-orb auth-orb-3"></div>

            <div className="auth-card">
                <div className="auth-brand">
                    <div className="auth-brand-icon">🚀</div>
                    <span className="auth-brand-name">INSULATED</span>
                </div>

                <h1 className="auth-title">
                    {show2faForm ? t('auth:two_factor.title') : t('auth:login.welcome')}
                </h1>
                <p className="auth-subtitle">
                    {show2faForm
                        ? t('auth:two_factor.subtitle_login')
                        : t('auth:login.subtitle')}
                </p>

                {message && (
                    <div className={`auth-alert ${show2faForm ? 'auth-alert-warning' : 'auth-alert-error'}`}>
                        {message}
                    </div>
                )}

                {show2faForm ? (
                    <form onSubmit={onSubmit2fa}>
                        <div className="auth-form-group">
                            <FontAwesomeIcon icon={faShieldAlt} className="auth-input-icon" />
                            <input
                                type="text"
                                id="otpToken"
                                className="auth-input"
                                placeholder={t('auth:two_factor.code_placeholder')}
                                name="otpToken"
                                value={otpToken}
                                onChange={onChange}
                                maxLength="6"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                required
                            />
                        </div>

                        <div className="auth-btn-group">
                            <button type="submit" className="auth-btn auth-btn-success">
                                <FontAwesomeIcon icon={faShieldAlt} />
                                {t('auth:two_factor.verify_button')}
                            </button>
                            <button
                                type="button"
                                className="auth-btn auth-btn-google"
                                onClick={() => setShow2faForm(false)}
                            >
                                {t('common:actions.back')}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={onSubmit}>
                        <div className="auth-form-group">
                            <FontAwesomeIcon icon={faUser} className="auth-input-icon" />
                            <input
                                type="text"
                                className="auth-input"
                                placeholder={t('auth:login.identifier_placeholder')}
                                name="identifier"
                                value={identifier}
                                onChange={onChange}
                                required
                            />
                        </div>

                        <div className="auth-form-group">
                            <FontAwesomeIcon icon={faLock} className="auth-input-icon" />
                            <input
                                type="password"
                                className="auth-input"
                                placeholder={t('auth:login.password_placeholder')}
                                name="password"
                                value={password}
                                onChange={onChange}
                                required
                            />
                        </div>

                        <div className="auth-btn-group">
                            <button type="submit" className="auth-btn auth-btn-primary">
                                <FontAwesomeIcon icon={faKey} />
                                {t('auth:login.submit_button')}
                            </button>
                        </div>

                        <div className="auth-divider">{t('common:common.or')}</div>

                        <button
                            type="button"
                            className="auth-btn auth-btn-google"
                            onClick={handleGoogleLogin}
                        >
                            <FontAwesomeIcon icon={faGoogle} style={{ color: '#ea4335' }} />
                            {t('auth:google_auth.google_button')}
                        </button>
                    </form>
                )}

                <div className="auth-links">
                    <div>
                        <Link to="/forgot-password" className="auth-link">
                            {t('auth:login.forgot_password_link')}
                        </Link>
                    </div>
                    <div>
                        {t('auth:login.no_account')}{' '}
                        <Link to="/register" className="auth-link">
                            {t('auth:login.register_link')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;